import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Popconfirm } from "antd";
import { useContext, useEffect, useState } from "react";
import { HomeContext } from "../../containers/Home";
import useFormHandler from "../../hooks/useFormHandler";
import usePermission from "../../hooks/usePermission";
import { useProjectStore } from "../../stores/projectStore";
import Emitter from "../../utils/emitter";
import { getCrudaClass } from "../../utils/lookup_list";
import { filterInitialForm } from "../../utils/utils";
import CustomForm from "../CustomForm";
import FormButtonSave from "../FormButtonSave";
import RACIModal from "./RACIModal";
import { alertMessage } from "../../utils/alert";

function OperationConfigModal(props: any) {
	const context: any = useContext(HomeContext);

	const { project } = useProjectStore();

	// form related
	const { formRef, formReset, setFormReset, initialForm, resetForm } =
		useFormHandler();

	const [activeRowKey, setActiveRowKey] = useState<any>(null);
	const [cancelPopover, setCancelPopover] = useState<boolean>(false);
	const [deletePopover, setDeletePopover] = useState<boolean>(false);
	const [currentProject, setCurrentProject] = useState<any>(null);

	const [operationInfo, setOperationInfo] = useState<any>(null);

	const [RACIModalOpen, setRACIModalOpen] = useState<boolean>(false);

	const hasDeleteOperationPermission = usePermission(
		"Delete.Operation",
		currentProject?.project_code
	);

	const hasEditOperationPermission = usePermission(
		"Edit.Operation",
		currentProject?.project_code
	);

	useEffect(() => {
		if (props.operationRecord) {
			resetForm(props.operationRecord);
			setActiveRowKey(props.operationRecord._id);
			setOperationInfo(props.operationRecord);
			setCurrentProject(props.currentProjectData);
			// console.log(props.operationRecord);
		}
	}, [props.operationRecord, props.currentProjectData]);

	const handleFormCancel = () => {
		props.setOpen(false);
		setCancelPopover(false);
		context?.handleFormUpdating(false, props.activeRowKey);
		if (operationInfo) {
			resetForm(operationInfo);
			setOperationInfo(operationInfo);
		} else {
			resetForm();
		}
	};

	const sanitizeValues = (values: any) => {
		Object.keys(values).forEach((key) => {
			if (typeof values[key] == "string") {
				values[key] = values[key].trim();
			}
		});
		return values;
	};

	// Add Operation Item Function
	const handleFormAdd = async (values: any) => {
		const cruda = await getCrudaClass("operation");
		Emitter.emit("loading", {
			name: "operation-config-modal",
			isLoading: true,
		});
		values = sanitizeValues(values);
		try {
			const addedItem = await cruda.addLookupItem(
				"operation",
				props?.option,
				values,
				{
					project_id: currentProject?._id,
					project_code: currentProject?.project_code,
					well_id: operationInfo?.well_id,
					project_template: currentProject?.project_template,
				},
				context?.currentTenantKey,
				null,
				props.user
			);
			alertMessage("success", "Operation has been successfully saved.");
			Emitter.emit("loading", {
				name: "operation-config-modal",
				isLoading: false,
			});
			context?.handleFormUpdating(false, "new");

			resetForm(addedItem);
			context.setCurrentWell(addedItem);

			setActiveRowKey(
				addedItem?.uuid ||
					addedItem?.group_uuid ||
					addedItem?.tag_uuid ||
					addedItem?._id
			);

			const newRecord = { ...operationInfo };

			newRecord.operation_data = [...operationInfo?.operation_data, addedItem];

			props.setOperationRecord((prev: any) => ({
				...prev,
				...addedItem,
				operation_data: newRecord.operation_data,
			}));
			Emitter.emit("resetForm", newRecord);
			Emitter.emit("forceUpdate", {
				data: { operation_data: newRecord.operation_data },
			});
		} catch (error: any) {
			if (error.code === 11000) {
				alertMessage(
					"error",
					`This Operation already exists, please ensure the Operation ${
						Object.keys(error.keyPattern)[0]
					} is unique. Changes are not saved.`
				);
			} else {
				alertMessage("error", "Changes are not saved. Please try again.");
			}
			Emitter.emit("loading", {
				name: "operation-config-modal",
				isLoading: false,
			});

			throw error;
		}
	};

	// Update Operation Item Function
	const handleFormUpdate = (values: any) => {
		getCrudaClass("operation").then((cruda: any) => {
			Emitter.emit("loading", {
				name: "operation-config-modal",
				isLoading: true,
			});
			values = sanitizeValues(values);
			const form = cruda.getFormList("operation");
			const formList: any = [];
			form.forEach((element: any) => {
				if (element.children && !("name" in element)) {
					element.children.forEach((child_element: any) => {
						if (child_element.name) formList.push(child_element.name);
					});
				} else {
					if (element.name) formList.push(element.name);
				}
			});
			cruda
				.updateLookupItem(
					"operation",
					props?.option,
					activeRowKey,
					context?.currentTenantKey,
					{ ...values },
					filterInitialForm(values, initialForm),
					{
						project_id: currentProject?._id,
						project_code: currentProject?.project_code,
						project_template: currentProject?.project_template,
					},
					props.user,
					formList
				)
				.then((updatedItem: any) => {
					alertMessage("success", "Operation has been successfully updated.");
					Emitter.emit("loading", {
						name: "operation-config-modal",
						isLoading: false,
					});
					context?.handleFormUpdating(false, activeRowKey);
					resetForm(updatedItem);
					context.setCurrentWell(updatedItem);
					const newOperationData = operationInfo?.operation_data?.map(
						(item: any) => {
							if (item._id === updatedItem._id) {
								return updatedItem;
							}
							return item;
						}
					);
					const newRecord = {
						...operationInfo,
						operation_data: newOperationData,
					};
					Emitter.emit("resetForm", newRecord);
					Emitter.emit("forceUpdate", {
						data: { operation_data: newOperationData },
					});
				})
				.catch((error: any) => {
					console.log(error);
					if (error?.status === 409) {
						alertMessage(
							"error",
							"This operation already exists, please ensure the operation name is unique. Changes are not saved."
						);
					} else {
						alertMessage("error", "Changes are not saved. Please try again.");
					}
					Emitter.emit("loading", {
						name: "operation-config-modal",
						isLoading: false,
					});
				});
		});
	};

	const handleFormSave = (values: any) => {
		if (activeRowKey) {
			handleFormUpdate(values); // Update Operation Item
		} else {
			if (values.project_template || values.operation_template) {
				setRACIModalOpen(true);
			} else {
				handleFormAdd(values); // Add Operation Item
			}
		}
	};

	const handleFormDelete = (values: any) => {
		context?.handleFormUpdating(false, activeRowKey);
		getCrudaClass("operation").then((cruda: any) => {
			const data = { is_removed: true };
			cruda
				.updateLookupItem(
					"operation",
					props?.option,
					activeRowKey,
					context?.currentTenantKey,
					data,
					null, //filterInitialForm(data, initialForm),
					{},
					props.user
				)
				.then((updatedItem: any) => {
					Emitter.emit("alert", {
						type: "success",
						message: `Operation has been successfully deleted.`,
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});

					const newOperationData = operationInfo?.operation_data?.filter(
						(item: any) => item._id !== activeRowKey
					);
					const newRecord = {
						...operationInfo,
						operation_data: newOperationData,
					};

					console.log("Updated Operation Data After Deletion:", newRecord);

					Emitter.emit("resetForm", newRecord);
					Emitter.emit("forceUpdate", {
						data: { operation_data: newOperationData },
					});

					resetForm(null);

					if (props.redirectOnDelete) {
						props.redirectOnDelete();
					}
				})
				.catch(() => {
					Emitter.emit("alert", {
						type: "error",
						message: "Changes are not saved. Please try again.",
						description: "",
						top: true,
						closeable: false,
						timeout: 3000,
					});
				})
				.finally(() => {
					props.setOpen(false);
					setDeletePopover(false);
					setActiveRowKey(null);
				});
		});
	};

	return (
		<>
			<Modal
				closable={false}
				title={"Well - operation configuration"}
				open={props.open}
				centered
				destroyOnClose
				footer={() => {
					return (
						<>
							{hasDeleteOperationPermission.hasPermission && (
								// Delete - Popconfirm
								<Popconfirm
									placement="topLeft"
									title={
										<div style={{ maxWidth: "300px", padding: "10px" }}>
											<>
												Permanently delete this well operation?
												<br />
												This action cannot be undone.
											</>
										</div>
									}
									open={deletePopover}
									onConfirm={handleFormDelete}
									overlayClassName="popconfirm-danger"
									onCancel={() => {
										setDeletePopover(false);
									}}
									okText="Delete"
									okType="danger"
									icon={<ExclamationCircleOutlined style={{ color: "grey" }} />}
									cancelText={<span>Cancel</span>}
									cancelButtonProps={{ ghost: true }}
								>
									{/* Delete Button */}
									<Button
										disabled={!activeRowKey}
										className="ant-btn-secondary"
										style={{ marginRight: "auto" }}
										danger
										onClick={() => {
											setDeletePopover(true);
										}}
									>
										Delete
									</Button>
								</Popconfirm>
							)}
							{/* Cancel Button - Popconfirm */}
							<Popconfirm
								icon={<ExclamationCircleOutlined style={{ color: "grey" }} />}
								title={
									<div style={{ maxWidth: "300px", padding: "10px" }}>
										<>
											You have unsaved changes. Discard them or continue
											editing?
										</>
									</div>
								}
								open={cancelPopover}
								placement="topRight"
								cancelButtonProps={{ ghost: true }}
								cancelText="Discard changes"
								onCancel={() => {
									setCancelPopover(false);
									setTimeout(() => {
										handleFormCancel();
									}, 300);
								}}
								okText="Continue editing"
								okType="default"
								onConfirm={() => {
									setCancelPopover(false);
								}}
							>
								{/* Cancel Button */}
								<Button
									className="ant-btn-secondary"
									onClick={() => {
										if (formReset) {
											handleFormCancel();
										} else {
											setCancelPopover(true);
										}
									}}
								>
									Cancel
								</Button>
							</Popconfirm>
							{/* Save Button */}
							{hasEditOperationPermission.hasPermission && (
								<FormButtonSave
									activeKey={activeRowKey}
									form={formRef}
									handleFormSave={handleFormSave}
									formReset={formReset}
									setFormReset={setFormReset}
								/>
							)}
						</>
					);
				}}
			>
				<div style={{ height: "50vh" }}>
					<CustomForm
						tabKey={"operation"}
						formOption={
							project?.project_scope == "multi-asset" ? "multi-asset" : null
						}
						formRef={formRef}
						setFormReset={setFormReset}
						dataOptions={props.extraData}
						activeRowKey={activeRowKey}
						activeRecord={props.operationRecord}
						readOnly={!hasEditOperationPermission.hasPermission}
					/>
				</div>
			</Modal>
			{RACIModalOpen && (
				<RACIModal
					open={RACIModalOpen}
					setOpen={setRACIModalOpen}
					template={formRef.getFieldValue("project_template")}
					extraData={props.extraData}
					operationFormRef={formRef}
					extraSaveFunction={async () => {
						try {
							await handleFormAdd(formRef.getFieldsValue());
						} catch (err) {
							console.log("extraSaveFunction failed parent", err);
							throw err;
						}
					}}
				/>
			)}
		</>
	);
}

export default OperationConfigModal;
